import * as React from "react";
import { Box, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { ActiveRallyVideo } from "./ActiveRallyVideo";
import {
  buildGamePointAssignments,
  GamePointAssignmentExample,
  TriagePlayerId,
  TRIAGE_PLAYER_IDS,
} from "./buildGamePointAssignments";
import {
  buildSingleTopFocusCard,
  SingleTopFocusCard,
} from "./buildSingleTopFocus";
import type { FirefightClip } from "./GameStory_firefights";
import type {
  PlayerIndexMap,
  RallyBadge,
  RenderPlayerIdentity,
} from "./GameStoryDialog_UPDATED";
import type { KitchenArrivalRow } from "./RallyBoardPopup";
import type { CvJson } from "../../shared/types/cvJson";
import type { InsightsJson } from "../../shared/types/insightsEtcTypes";
import type { PounceWindowRow } from "../../shared/types/pounceEtc";

type ReviewClipSource = "focus" | "scoring";
type ReviewIntent = "fix" | "keep" | "understand" | "verify";

type ReviewClip = {
  id: string;
  source: ReviewClipSource;
  intent: ReviewIntent;
  intentLabel: string;
  lookFor: string;
  practiceTakeaway: string;
  rallyIndex: number;
  shotNumber: number | null;
  playerId: TriagePlayerId;
  label: string;
  headline: string;
  why: string[];
  score: number;
  scoreParts: string[];
  startMs: number | null;
  endMs: number | null;
};

function intentColor(intent: ReviewIntent): string {
  switch (intent) {
    case "fix":
      return "rgba(185,85,45,0.96)";
    case "keep":
      return "rgba(35,140,75,0.96)";
    case "understand":
      return "rgba(115,80,170,0.96)";
    default:
      return "rgba(110,110,110,0.96)";
  }
}

function intentBg(intent: ReviewIntent): string {
  switch (intent) {
    case "fix":
      return "rgba(185,85,45,0.10)";
    case "keep":
      return "rgba(35,140,75,0.10)";
    case "understand":
      return "rgba(115,80,170,0.10)";
    default:
      return "rgba(0,0,0,0.06)";
  }
}

function intentLabel(intent: ReviewIntent): string {
  switch (intent) {
    case "fix":
      return "Fix this";
    case "keep":
      return "Keep this";
    case "understand":
      return "Understand";
    default:
      return "Verify";
  }
}

function teamOfPlayerId(playerId: number): 0 | 1 {
  return playerId < 2 ? 0 : 1;
}

function scoreText(score: [number, number] | null | undefined): string {
  if (!score) return "?";
  return `${score[0]}-${score[1]}`;
}

function getRally(rowByIndex: Map<number, KitchenArrivalRow>, rallyIndex: number) {
  return rowByIndex.get(rallyIndex) ?? null;
}

function getOverlayWindow(row: KitchenArrivalRow | null, shotNumber: number | null) {
  if (!row || shotNumber == null) return { startMs: null, endMs: null };
  const overlays = row.shotNumberOverlays ?? [];
  const ov = overlays.find((s) => s.shotNumber === shotNumber);
  const next = overlays.find((s) => s.shotNumber === shotNumber + 1);
  if (!ov || typeof ov.atMsRel !== "number") {
    return { startMs: null, endMs: null };
  }
  const segmentStartMs = row.segmentStartMs ?? row.startMs ?? 0;
  const startMs = segmentStartMs + ov.atMsRel;
  const endMs =
    next && typeof next.atMsRel === "number"
      ? segmentStartMs + next.atMsRel
      : startMs + 1200;
  return {
    startMs,
    endMs,
  };
}

function leverageScore(row: KitchenArrivalRow | null): { score: number; label: string | null } {
  if (!row) return { score: 0, label: null };
  const before = row.scoreBefore;
  const high = Math.max(before?.[0] ?? 0, before?.[1] ?? 0);
  const margin = Math.abs((before?.[0] ?? 0) - (before?.[1] ?? 0));

  if (high >= 9 && margin <= 2) {
    return { score: 14, label: "late close-score rally" };
  }
  if (high >= 7 && margin <= 2) {
    return { score: 9, label: "close-score rally" };
  }
  if (margin <= 1) {
    return { score: 5, label: "tight score" };
  }
  return { score: 0, label: null };
}

function assignmentStrength(ex: GamePointAssignmentExample): {
  score: number;
  label: string;
} {
  switch (ex.reasonCode) {
    case "missedServe":
    case "missedReturn":
    case "missed3rdDrop":
    case "missed3rdDrive":
    case "faultNet":
    case "faultOut":
    case "unforcedError":
    case "terminal_winner":
      return { score: 18, label: "direct attribution" };
    case "popupExploited":
      return { score: 15, label: "popup attribution" };
    case "forcedFault":
    case "forced_error_drawn":
      return { score: 12, label: "pressure attribution" };
    case "setup_shot":
      return { score: 9, label: "setup-shot attribution" };
    default:
      return { score: 3, label: "fallback attribution" };
  }
}

function getScoringClipIntent(ex: GamePointAssignmentExample): ReviewIntent {
  if (
    ex.bucketKey === "game_points_won" ||
    ex.bucketKey === "opponent_opportunities_stymied"
  ) {
    return "keep";
  }

  if (
    ex.reasonCode === "unknown" ||
    ex.reasonCode === "setup_shot" ||
    ex.reasonCode === "terminal_error"
  ) {
    return "verify";
  }

  return "fix";
}

function getScoringLookFor(ex: GamePointAssignmentExample): string {
  switch (ex.bucketKey) {
    case "game_points_lost":
      return "Watch the attributed moment and the setup just before it.";
    case "opportunities_lost":
      return "Watch how the serving chance slipped away.";
    case "game_points_won":
      return "Watch what helped your team finish the scoring rally.";
    case "opponent_opportunities_stymied":
      return "Watch how your team stopped the opponent from scoring on serve.";
    default:
      return "Watch the assigned moment and check whether it matches what happened.";
  }
}

function getScoringPracticeTakeaway(ex: GamePointAssignmentExample): string {
  switch (ex.reasonCode) {
    case "missedServe":
      return "Try: make-first-serve reps before adding pace or placement.";
    case "missedReturn":
      return "Try: deep, high-margin returns before adding direction.";
    case "missed3rdDrop":
      return "Try: third-shot drops with safe clearance but lower finish.";
    case "missed3rdDrive":
      return "Try: third-shot drives with margin, then play the fifth ball.";
    case "popupExploited":
      return "Try: lower reset/dink shape under pressure.";
    case "forcedFault":
    case "forced_error_drawn":
      return "Try: survive the pressure ball first; counter only when balanced.";
    case "terminal_winner":
      return "Keep: notice the setup and contact height that made this work.";
    case "faultNet":
    case "faultOut":
    case "unforcedError":
      return "Try: rebuild the shot with more margin before going bigger.";
    default:
      return "Check: confirm whether this assigned moment is actually the useful review point.";
  }
}

function getFocusClipIntent(args: {
  headline: string;
  row: KitchenArrivalRow | null;
  selectedPlayerId: TriagePlayerId;
}): ReviewIntent {
  const { headline, row, selectedPlayerId } = args;
  const team = teamOfPlayerId(selectedPlayerId);
  const teamWon = row?.winnerTeam === team;
  const text = headline.toLowerCase();

  if (teamWon) {
    return text.includes("not stopping") ||
      text.includes("sitting up") ||
      text.includes("sat up")
      ? "understand"
      : "keep";
  }

  if (
    text.includes("not stopping") ||
    text.includes("sitting up") ||
    text.includes("lost") ||
    text.includes("miss") ||
    text.includes("short returns")
  ) {
    return "fix";
  }
  if (
    text.includes("scored") ||
    text.includes("won") ||
    text.includes("denied") ||
    text.includes("finished")
  ) {
    return "keep";
  }
  return "understand";
}

function getFocusLookFor(headline: string): string {
  const text = headline.toLowerCase();
  if (text.includes("pressure")) {
    return "Watch whether the pressure ball made the next shot easier or harder.";
  }
  if (text.includes("sitting up") || text.includes("sat up")) {
    return "Watch whether the ball sat high enough for the opponent to attack.";
  }
  if (text.includes("short return")) {
    return "Watch return depth and whether the serving team attacked the next ball.";
  }
  if (text.includes("third-shot")) {
    return "Watch the third-shot shape and the opponent's next contact point.";
  }
  return "Watch for the decision point that seems to repeat.";
}

function getFocusPracticeTakeaway(headline: string): string {
  const text = headline.toLowerCase();
  if (text.includes("pressure")) {
    return "Try: block/reset reps under pace. Keep the reply lower and simpler.";
  }
  if (text.includes("sitting up") || text.includes("sat up")) {
    return "Try: replay this shot shape lower, then recover before attacking.";
  }
  if (text.includes("short return")) {
    return "Try: deep return reps, then play return-plus-one live.";
  }
  if (text.includes("third-shot")) {
    return "Try: third-shot shape reps, choosing drop or drive by ball height.";
  }
  return "Try: recreate the first few shots and test one adjustment.";
}

function buildScoringClips(args: {
  rows: KitchenArrivalRow[];
  selectedPlayerId: TriagePlayerId;
  badgesByRallyIndex?: Map<number, RallyBadge[]>;
  pounceWindowRows?: PounceWindowRow[];
}): ReviewClip[] {
  const { rows, selectedPlayerId, badgesByRallyIndex, pounceWindowRows } = args;
  const rowByIndex = new Map(rows.map((r) => [r.rallyIndex, r]));
  const assignments = buildGamePointAssignments({
    rows,
    badgesByRallyIndex,
    pounceWindowRows,
  });
  const buckets = assignments.byPlayerId[selectedPlayerId];

  const examples: GamePointAssignmentExample[] = [
    ...(buckets.gamePointsLost?.examples ?? []),
    ...(buckets.gamePointsWon?.examples ?? []),
    ...(buckets.opportunitiesLost?.examples ?? []),
    ...(buckets.opponentOpportunitiesStymied?.examples ?? []),
  ];

  return examples.map((ex) => {
    const row = getRally(rowByIndex, ex.rallyIndex);
    const strength = assignmentStrength(ex);
    const leverage = leverageScore(row);
    const shotNumber = ex.focusShotNumber ?? ex.terminalShotNumber ?? null;
    const window = getOverlayWindow(row, shotNumber);
    const intent = getScoringClipIntent(ex);
    const pointWasAwarded =
      ex.bucketKey === "game_points_lost" || ex.bucketKey === "game_points_won";
    const scoreParts = [
      pointWasAwarded ? "point awarded" : "no point awarded",
      strength.label,
      leverage.label,
      ex.bucketKey === "opportunities_lost" ? "serve chance lost" : null,
      ex.bucketKey === "opponent_opportunities_stymied"
        ? "opponent serve chance denied"
        : null,
    ].filter((s): s is string => !!s);

    const score =
      (pointWasAwarded ? 40 : 28) +
      strength.score +
      leverage.score +
      (ex.bucketKey === "opportunities_lost" ? 8 : 0) +
      (ex.bucketKey === "opponent_opportunities_stymied" ? 5 : 0);

    return {
      id: `scoring-${ex.bucketKey}-${ex.rallyIndex}-${shotNumber ?? "x"}`,
      source: "scoring",
      intent,
      intentLabel: intentLabel(intent),
      lookFor: getScoringLookFor(ex),
      practiceTakeaway: getScoringPracticeTakeaway(ex),
      rallyIndex: ex.rallyIndex,
      shotNumber,
      playerId: selectedPlayerId,
      label: `Rally ${ex.rallyIndex + 1}`,
      headline: `${ex.reasonLabel} (${scoreText(ex.scoreBefore)} -> ${scoreText(
        ex.scoreAfter,
      )})`,
      why: [
        `A point was awarded and the attribution rules assigned this scoring rally to p${selectedPlayerId}.`,
        ex.reasonText ? `Assignment focus: ${ex.reasonText}.` : "",
        ...ex.rationale.slice(0, 2),
      ].filter(Boolean),
      score,
      scoreParts,
      startMs: window.startMs,
      endMs: window.endMs,
    };
  });
}

function buildFocusClips(args: {
  rows: KitchenArrivalRow[];
  focusCard: SingleTopFocusCard | null;
  selectedPlayerId: TriagePlayerId;
}): ReviewClip[] {
  const { rows, focusCard, selectedPlayerId } = args;
  const rowByIndex = new Map(rows.map((r) => [r.rallyIndex, r]));
  if (!focusCard) return [];

  return focusCard.evidence.map((ev, idx) => {
    const row = getRally(rowByIndex, ev.rallyIndex);
    const leverage = leverageScore(row);
    const headline = focusCard.displayLabel || focusCard.label;
    const intent = getFocusClipIntent({
      headline,
      row,
      selectedPlayerId,
    });
    const scoreParts = [
      "repeated player pattern",
      "trainable theme",
      row?.scoredPoint ? "point awarded" : null,
      leverage.label,
      ev.ruleLabel,
    ].filter((s): s is string => !!s);

    return {
      id: `focus-${ev.rallyIndex}-${ev.shotNumber}-${idx}`,
      source: "focus",
      intent,
      intentLabel: intentLabel(intent),
      lookFor: getFocusLookFor(headline),
      practiceTakeaway: getFocusPracticeTakeaway(headline),
      rallyIndex: ev.rallyIndex,
      shotNumber: ev.shotNumber,
      playerId: selectedPlayerId,
      label: `Rally ${ev.rallyIndex + 1}`,
      headline,
      why: [
        `This clip supports the top player-specific theme: ${focusCard.displayLabel || focusCard.label}.`,
        ev.whyItCounts,
        row?.scoredPoint ? "This also happened in a rally where a point was awarded." : "",
      ].filter(Boolean),
      score:
        46 +
        Math.min(18, (ev.scoreFactors ?? []).reduce((sum, f) => sum + f.value, 0)) +
        (row?.scoredPoint ? 8 : 0) +
        leverage.score,
      scoreParts,
      startMs: ev.startMs,
      endMs: ev.endMs,
    };
  });
}

function dedupeAndRankClips(clips: ReviewClip[]): ReviewClip[] {
  const byKey = new Map<string, ReviewClip>();
  for (const clip of clips) {
    const key = `${clip.rallyIndex}:${clip.shotNumber ?? "x"}`;
    const prev = byKey.get(key);
    if (!prev || clip.score > prev.score) byKey.set(key, clip);
  }
  return Array.from(byKey.values()).sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.rallyIndex - b.rallyIndex;
  });
}

export function ThreeMinuteReviewPopup(props: {
  open: boolean;
  onClose: () => void;
  packageId: string;
  rows: KitchenArrivalRow[];
  insightsJson: InsightsJson | null;
  cvJson: CvJson | null | undefined;
  renderPlayerIdentity: RenderPlayerIdentity;
  insightsToCvPlayerIndex: PlayerIndexMap;
  firefightsClips: FirefightClip[];
  firefightRallySet: Set<number>;
  badgesByRallyIndex?: Map<number, RallyBadge[]>;
  pounceWindowRows?: PounceWindowRow[];
}) {
  const {
    open,
    onClose,
    packageId,
    rows,
    insightsJson,
    cvJson,
    renderPlayerIdentity,
    insightsToCvPlayerIndex,
    firefightsClips,
    firefightRallySet,
    badgesByRallyIndex,
    pounceWindowRows,
  } = props;

  const [selectedPlayerId, setSelectedPlayerId] =
    React.useState<TriagePlayerId>(0);
  const [selectedClipIndex, setSelectedClipIndex] = React.useState(0);

  const focusCard = React.useMemo(() => {
    if (!insightsJson) return null;
    return buildSingleTopFocusCard({
      insightsJson,
      cvJson,
      selectedPlayerId,
    });
  }, [insightsJson, cvJson, selectedPlayerId]);

  const clips = React.useMemo(() => {
    return dedupeAndRankClips([
      ...buildFocusClips({ rows, focusCard, selectedPlayerId }),
      ...buildScoringClips({
        rows,
        selectedPlayerId,
        badgesByRallyIndex,
        pounceWindowRows,
      }),
    ]).slice(0, 5);
  }, [rows, focusCard, selectedPlayerId, badgesByRallyIndex, pounceWindowRows]);

  React.useEffect(() => {
    setSelectedClipIndex(0);
  }, [selectedPlayerId]);

  React.useEffect(() => {
    if (selectedClipIndex >= clips.length) setSelectedClipIndex(0);
  }, [clips.length, selectedClipIndex]);

  if (!open) return null;

  const selectedClip = clips[selectedClipIndex] ?? null;
  const selectedRow =
    selectedClip == null
      ? null
      : rows.find((r) => r.rallyIndex === selectedClip.rallyIndex) ?? null;
  const team = teamOfPlayerId(selectedPlayerId);

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 18,
        zIndex: 4300,
        bgcolor: "white",
        color: "rgba(0,0,0,0.88)",
        border: "1px solid rgba(0,0,0,0.2)",
        borderRadius: 1,
        boxShadow: "0 18px 70px rgba(0,0,0,0.36)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 1.35,
          py: 0.85,
          borderBottom: "1px solid rgba(0,0,0,0.12)",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box sx={{ fontSize: 18, fontWeight: 900 }}>
          3-Minute Player Review
        </Box>
        <Box sx={{ fontSize: 12.5, color: "rgba(0,0,0,0.6)" }}>
          Best first clips for one player, ranked by recurrence, scoring impact,
          attribution strength, and trainability.
        </Box>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          px: 1.1,
          py: 0.75,
          borderBottom: "1px solid rgba(0,0,0,0.1)",
          display: "flex",
          gap: 0.75,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ fontSize: 12.5, fontWeight: 800, mr: 0.2 }}>Player</Box>
        {TRIAGE_PLAYER_IDS.map((playerId) => {
          const selected = selectedPlayerId === playerId;
          const playerTeam = teamOfPlayerId(playerId);
          return (
            <Button
              key={playerId}
              size="small"
              variant={selected ? "contained" : "outlined"}
              onClick={() => setSelectedPlayerId(playerId)}
              sx={{
                textTransform: "none",
                minWidth: 76,
                px: 0.7,
                py: 0.35,
                bgcolor: selected
                  ? playerTeam === 0
                    ? "rgba(60,145,235,0.92)"
                    : "rgba(45,165,90,0.92)"
                  : "transparent",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.55 }}>
                <Box sx={{ fontWeight: 900 }}>p{playerId}</Box>
                {renderPlayerIdentity({
                  playerId,
                  size: 34,
                  fontSize: 10,
                })}
              </Box>
            </Button>
          );
        })}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
        <Box
          sx={{
            width: 430,
            minWidth: 430,
            borderRight: "1px solid rgba(0,0,0,0.12)",
            overflowY: "auto",
            p: 1,
          }}
        >
          <Box sx={{ fontSize: 18, fontWeight: 900, mb: 0.35 }}>
            p{selectedPlayerId}: triage path
          </Box>
          <Box sx={{ fontSize: 13, color: "rgba(0,0,0,0.62)", mb: 1 }}>
            Team {team}. Each clip says whether to fix it, keep it, or use it
            to understand a repeated pattern.
          </Box>

          {clips.length ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.7 }}>
              {clips.map((clip, idx) => {
                const selected = idx === selectedClipIndex;
                return (
                  <Button
                    key={clip.id}
                    variant="outlined"
                    onClick={() => setSelectedClipIndex(idx)}
                    sx={{
                      justifyContent: "stretch",
                      textAlign: "left",
                      textTransform: "none",
                      borderColor: selected
                        ? "rgba(25,118,210,0.72)"
                        : "rgba(0,0,0,0.16)",
                      bgcolor: selected ? "rgba(25,118,210,0.08)" : "white",
                      color: "rgba(0,0,0,0.84)",
                      p: 0.85,
                    }}
                  >
                    <Box sx={{ width: "100%", minWidth: 0 }}>
                      <Box sx={{ display: "flex", gap: 0.7, mb: 0.35 }}>
                        <Box sx={{ fontWeight: 900 }}>#{idx + 1}</Box>
                        <Box sx={{ fontWeight: 900, flex: 1 }}>
                          {clip.label}
                          {clip.shotNumber ? ` • Shot ${clip.shotNumber}` : ""}
                        </Box>
                        <Box
                          sx={{
                            fontSize: 11,
                            fontWeight: 900,
                            color: intentColor(clip.intent),
                          }}
                        >
                          {clip.intentLabel}
                        </Box>
                      </Box>
                      <Box sx={{ fontSize: 13, fontWeight: 800, mb: 0.25 }}>
                        {clip.headline}
                      </Box>
                      <Box
                        sx={{
                          fontSize: 12,
                          color: "rgba(0,0,0,0.66)",
                          mb: 0.35,
                        }}
                      >
                        {clip.lookFor}
                      </Box>
                      <Box sx={{ fontSize: 11.5, color: "rgba(0,0,0,0.54)" }}>
                        {clip.scoreParts.slice(0, 3).join(" • ")}
                      </Box>
                    </Box>
                  </Button>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ fontSize: 13, color: "rgba(0,0,0,0.62)" }}>
              No deterministic review clips were found for this player.
            </Box>
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              px: 1.15,
              py: 0.8,
              borderBottom: "1px solid rgba(0,0,0,0.1)",
            }}
          >
            <Box sx={{ fontSize: 18, fontWeight: 900 }}>
              {selectedClip
                ? `${selectedClip.label}${
                    selectedClip.shotNumber
                      ? ` • Shot ${selectedClip.shotNumber}`
                      : ""
                  }`
                : "No clip selected"}
            </Box>
            {selectedClip ? (
              <Box sx={{ mt: 0.35, display: "flex", gap: 0.65, flexWrap: "wrap" }}>
                <Box
                  sx={{
                    px: 0.75,
                    py: 0.22,
                    borderRadius: 1,
                    bgcolor: intentBg(selectedClip.intent),
                    color: intentColor(selectedClip.intent),
                    fontSize: 11.5,
                    fontWeight: 900,
                  }}
                >
                  {selectedClip.intentLabel}
                </Box>
                {selectedClip.scoreParts.map((part) => (
                  <Box
                    key={part}
                    sx={{
                      px: 0.7,
                      py: 0.2,
                      borderRadius: 1,
                      bgcolor: "rgba(0,0,0,0.055)",
                      fontSize: 11.5,
                      fontWeight: 800,
                      color: "rgba(0,0,0,0.62)",
                    }}
                  >
                    {part}
                  </Box>
                ))}
              </Box>
            ) : null}
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
            <Box sx={{ flex: 1, minWidth: 0, bgcolor: "black" }}>
              {selectedRow && selectedClip ? (
                <ActiveRallyVideo
                  aiOpenViaParent={false}
                  insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                  renderPlayerIdentity={renderPlayerIdentity}
                  aiContextLabel="3-Minute Player Review"
                  focusedFilter={null}
                  packageId={packageId}
                  row={selectedRow}
                  rows={rows}
                  activeShotNumber={null}
                  activeShotExtraText={undefined}
                  shotOverlayPersistentExtraText={undefined}
                  showCondensedTimeline={false}
                  fontColor="white"
                  controlsPlacement="left"
                  firefightsClips={firefightsClips}
                  firefightRallySet={firefightRallySet}
                  showRateControls={true}
                  showTitle={true}
                  showScoreEtc={true}
                  showAi={true}
                  aiDrawerMode="split"
                  showLoopScrubber={true}
                  defaultLoopUiOpen={false}
                  showPopoutButton={true}
                  isInFullPopup={false}
                  seedPlayback={undefined}
                  seedLoopRegion={undefined}
                />
              ) : null}
            </Box>

            <Box
              sx={{
                width: 330,
                minWidth: 330,
                borderLeft: "1px solid rgba(0,0,0,0.12)",
                p: 1.1,
                overflowY: "auto",
              }}
            >
              {selectedClip ? (
                <>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignSelf: "flex-start",
                      px: 0.8,
                      py: 0.28,
                      borderRadius: 1,
                      bgcolor: intentBg(selectedClip.intent),
                      color: intentColor(selectedClip.intent),
                      fontSize: 12,
                      fontWeight: 900,
                      mb: 0.85,
                    }}
                  >
                    {selectedClip.intentLabel}
                  </Box>

                  <Box sx={{ fontSize: 14, fontWeight: 900, mb: 0.35 }}>
                    What to look for
                  </Box>
                  <Box
                    sx={{
                      fontSize: 13,
                      lineHeight: 1.35,
                      color: "rgba(0,0,0,0.7)",
                      mb: 1.1,
                    }}
                  >
                    {selectedClip.lookFor}
                  </Box>

                  <Box sx={{ fontSize: 14, fontWeight: 900, mb: 0.35 }}>
                    Practice takeaway
                  </Box>
                  <Box
                    sx={{
                      fontSize: 13,
                      lineHeight: 1.35,
                      color: "rgba(0,0,0,0.7)",
                      mb: 1.1,
                    }}
                  >
                    {selectedClip.practiceTakeaway}
                  </Box>

                  <Box sx={{ fontSize: 14, fontWeight: 900, mb: 0.55 }}>
                    Why selected
                  </Box>
                  <Box sx={{ fontSize: 13, fontWeight: 800, mb: 0.6 }}>
                    {selectedClip.headline}
                  </Box>
                  <Box
                    component="ul"
                    sx={{
                      m: 0,
                      pl: 2,
                      fontSize: 13,
                      lineHeight: 1.35,
                      color: "rgba(0,0,0,0.68)",
                    }}
                  >
                    {selectedClip.why.map((line, idx) => (
                      <li key={`${selectedClip.id}-why-${idx}`}>{line}</li>
                    ))}
                  </Box>
                  <Box sx={{ mt: 1, fontSize: 12, color: "rgba(0,0,0,0.54)" }}>
                    Priority score {Math.round(selectedClip.score)} from:{" "}
                    {selectedClip.scoreParts.join(", ")}.
                  </Box>
                </>
              ) : (
                <Box sx={{ fontSize: 13, color: "rgba(0,0,0,0.62)" }}>
                  Pick a clip to see why it was selected.
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
